import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  API_BASE,
  DEFAULT_GA4_ID,
  DEFAULT_GTM_ID,
  DEFAULT_ANALYTICS_MODE,
} from "../config";
import { configureAnalytics, trackPageView } from "../lib/analytics";

const buildFallbackSettings = () => ({
  analyticsEnabled: Boolean(DEFAULT_GTM_ID || DEFAULT_GA4_ID),
  providerMode: DEFAULT_ANALYTICS_MODE,
  gtmId: DEFAULT_GTM_ID,
  ga4Id: DEFAULT_GA4_ID,
});

const normalizeSettingsRecord = (record = {}) => ({
  analyticsEnabled: Boolean(record.analyticsEnabled),
  providerMode: record.providerMode || "",
  gtmId: record.gtmId || "",
  ga4Id: record.ga4Id || "",
});

const AnalyticsManager = () => {
  const location = useLocation();
  const hasConfigured = useRef(false);
  const lastTrackedPath = useRef("");

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      const fallbackSettings = buildFallbackSettings();

      try {
        const response = await fetch(
          `${API_BASE}/collections/site_settings/records?perPage=1&filter=${encodeURIComponent(
            'key = "default" && active = true'
          )}`
        );

        if (!response.ok) {
          throw new Error("No se pudo cargar site_settings");
        }

        const payload = await response.json();
        const firstRecord = Array.isArray(payload?.items) ? payload.items[0] : null;
        const settings = firstRecord
          ? normalizeSettingsRecord(firstRecord)
          : fallbackSettings;

        if (!cancelled) {
          configureAnalytics(settings);
          hasConfigured.current = true;
        }
      } catch {
        if (!cancelled) {
          configureAnalytics(fallbackSettings);
          hasConfigured.current = true;
        }
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hasConfigured.current) {
      return;
    }

    const currentPath = `${location.pathname}${location.search}${location.hash}` || "/";
    if (lastTrackedPath.current === currentPath) {
      return;
    }

    trackPageView({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      title: document.title,
    });

    lastTrackedPath.current = currentPath;
  }, [location.pathname, location.search, location.hash]);

  return null;
};

export default AnalyticsManager;
